import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import Cookies from "js-cookie";
import {jwtDecode} from "jwt-decode";
import { signin, getRefreshTokens } from "../services/auth.service";
import config from "../config";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem("refreshTokenModeration");
    Cookies.remove("accessTokenModeration", { secure: true });
    if (isMountedRef.current) {
      setUser(null);
      setAccessToken(null);
    }
    clearRefreshTimer();
  }, []);

  const handleAuthData = useCallback((data) => {
    if (data && data.accessToken) {
      const decoded = jwtDecode(data.accessToken);
      
      // Mirroring the check in LoginPage.jsx
      if (decoded.role !== "admin") {
        throw new Error("You do not possess the sufficient permissions to access the moderation tools.");
      }

      if (isMountedRef.current) {
        setUser(decoded);
        setAccessToken(data.accessToken);
      }

      // Persist tokens
      Cookies.set("accessTokenModeration", data.accessToken, { secure: true });
      if (data.refreshToken) {
        localStorage.setItem("refreshTokenModeration", data.refreshToken);
      }

      // Set up refresh timer
      const defaultLifetimeMs = config.isLocalOrIntegrationEnv ? 300_000 : 3_600_000; // 5min in test, 1 hour in prod
      const now = Math.round(Date.now() / 1000);
      const timeLeft = (decoded.exp - now - 15 * 60) * 1000;
      const refreshDelay = (timeLeft > 0) ? timeLeft : defaultLifetimeMs;
      
      clearRefreshTimer();
      refreshTimerRef.current = setTimeout(() => {
        refresh();
      }, refreshDelay);
    } else {
      logout();
    }
  }, [logout]);

  const refresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      const data = await getRefreshTokens();
      if (data) {
        handleAuthData(data);
      } else {
        logout();
      }
    } catch (error) {
      console.error("Token refresh failed", error);
      logout();
    } finally {
      isRefreshingRef.current = false;
    }
  }, [handleAuthData, logout]);

  const login = async (email, password, captchaResponse) => {
    const data = await signin(email, password, captchaResponse);
    if (data && !data.error) {
      try {
        handleAuthData(data);
        return data;
      } catch (error) {
        return { error: error.message };
      }
    } else {
      return { error: data.message };
    }
  };

  // Initial load
  useEffect(() => {
    const initAuth = async () => {
      const token = Cookies.get("accessTokenModeration");
      const refreshToken = localStorage.getItem("refreshTokenModeration");
      if (token && refreshToken) {
        try {
          const decoded = jwtDecode(token);
          const now = Math.round(Date.now() / 1000);
          
          if (decoded.exp > now) {
            handleAuthData({ accessToken: token, refreshToken });
          } else {
            await refresh();
          }
        } catch (e) {
          await refresh();
        }
      } else if (refreshToken) {
        await refresh();
      }
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [handleAuthData, refresh]);

  // Visibility change listener
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const token = Cookies.get("accessTokenModeration");
        if (token) {
          const decoded = jwtDecode(token);
          const now = Math.round(Date.now() / 1000);
          // If token is expired or expires in less than 5 minutes
          if (decoded.exp <= now + 300) {
            refresh();
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refresh]);

  const value = {
    user,
    accessToken,
    isLogged: !!accessToken,
    isLoading,
    login,
    logout,
    refresh
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
